import { useEffect, useState, useCallback, useRef } from 'react';
import { AlertTriangle, X, Upload, RotateCw } from 'lucide-react';
import { Button } from './ui/button';
import { filesApi } from '../lib/api';
import { useChunkedUpload, UPLOAD_STATUS } from '../hooks/useChunkedUpload';
import { toast } from 'sonner';

/**
 * StalledUploadsBanner
 *
 * Surfaces chunked-upload sessions that have been `status="uploading"` for
 * 10+ minutes — typically caused by browser-tab close, network drop, or a
 * Web Worker crash on very large files (e.g. ~700 MB IMAGE.DAT). Without
 * this banner the file silently disappears from the Master Files list and
 * the customer is left wondering why the order looks "Missing".
 *
 * For each stalled session the row shows:
 *   • Slot label (e.g. "Disc 2") + filename
 *   • Percent complete + raw bytes + age
 *   • "Re-upload" — opens a hidden file picker scoped to the SAME slot
 *     category, drives the existing chunked-upload pipeline, then dismisses
 *     the stalled session on success.
 *   • "Dismiss" — soft-cancels the session (status="cancelled" on backend,
 *     forensics preserved) and removes the row.
 */
const formatBytes = (n) => {
    if (!n) return '0 B';
    const u = ['B', 'KB', 'MB', 'GB'];
    let v = n, i = 0;
    while (v >= 1024 && i < u.length - 1) {
        v /= 1024;
        i += 1;
    }
    return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${u[i]}`;
};

const formatAge = (iso) => {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    const minutes = Math.max(1, Math.round((Date.now() - then) / 60000));
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} h ago`;
    return `${Math.round(hours / 24)} d ago`;
};

/**
 * One stalled-upload row. Owns its own `useChunkedUpload` instance keyed to
 * the session's category so clicking "Re-upload" hands the new file off to
 * the standard chunked pipeline (same code path the slot's normal uploader
 * uses).
 */
function StalledUploadRow({ session, orderId, onDismiss, onDismissAndRefresh }) {
    const fileInputRef = useRef(null);
    const [isDismissing, setIsDismissing] = useState(false);
    const cleanupRanRef = useRef(false);

    const { uploads, startUpload, isUploading } = useChunkedUpload(
        orderId,
        session.category,
        // Pass undefined for onComplete — we drive completion detection from
        // the uploads array effect below so stale closures (which previously
        // left the banner stuck after a successful re-upload) can't bite us.
        undefined,
    );
    const activeUpload = uploads[uploads.length - 1];
    const isCompleted = activeUpload?.status === UPLOAD_STATUS.COMPLETED;

    // Once the user has triggered a re-upload AND it has reached COMPLETED,
    // soft-cancel the original stalled session and tell the parent to
    // refresh. Guarded by `cleanupRanRef` so we only fire the cleanup once.
    useEffect(() => {
        if (!isCompleted || cleanupRanRef.current) return;
        cleanupRanRef.current = true;
        (async () => {
            try {
                await filesApi.cancelStalledUpload(orderId, session.id);
            } catch {
                // Non-fatal: file is already uploaded, banner will clear on next refetch.
            }
            // Optimistically remove the row from local state so the user
            // gets instant feedback even if the parent refresh is slow.
            onDismiss(session.id);
            onDismissAndRefresh();
            toast.success('Re-upload complete');
        })();
        // We intentionally exclude onDismissAndRefresh from deps — it may
        // change on parent re-render but the cleanup is idempotent via the ref.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCompleted, orderId, session.id]);

    const handleReupload = () => fileInputRef.current?.click();

    const handleFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        cleanupRanRef.current = false; // allow cleanup again for this new upload
        startUpload(file);
        e.target.value = '';
    };

    const handleDismiss = async () => {
        setIsDismissing(true);
        try {
            await filesApi.cancelStalledUpload(orderId, session.id);
            onDismiss(session.id);
            toast.success('Interrupted upload dismissed');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to dismiss');
        } finally {
            setIsDismissing(false);
        }
    };

    return (
        <li
            className="rounded border border-amber-200 bg-white px-3 py-2"
            data-testid={`stalled-upload-${session.id}`}
        >
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <Upload className="h-4 w-4 text-amber-600 shrink-0" />
                    <div className="min-w-0">
                        <p
                            className="text-sm font-medium truncate"
                            data-testid={`stalled-upload-name-${session.id}`}
                        >
                            {session.slot_label && (
                                <span className="text-amber-700 mr-1.5">
                                    {session.slot_label}
                                </span>
                            )}
                            <span className="text-amber-900">·</span>{' '}
                            <span className="font-mono">{session.filename || 'Unknown file'}</span>
                        </p>
                        <p className="text-xs text-slate-600">
                            <span data-testid={`stalled-upload-percent-${session.id}`}>
                                {session.percent_complete}% complete
                            </span>
                            <span className="mx-1">·</span>
                            {formatBytes(session.uploaded_bytes)} / {formatBytes(session.size)}
                            <span className="mx-1">·</span>
                            started {formatAge(session.created_at)}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        onChange={handleFile}
                        data-testid={`stalled-upload-input-${session.id}`}
                    />
                    <Button
                        size="sm"
                        variant="secondary"
                        className="bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300"
                        onClick={handleReupload}
                        disabled={isUploading}
                        data-testid={`stalled-upload-reupload-${session.id}`}
                    >
                        <RotateCw className="h-4 w-4 mr-1" />
                        Re-upload
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        className="text-amber-800 hover:bg-amber-100"
                        onClick={handleDismiss}
                        disabled={isDismissing || isUploading}
                        data-testid={`stalled-upload-dismiss-${session.id}`}
                    >
                        <X className="h-4 w-4 mr-1" />
                        Dismiss
                    </Button>
                </div>
            </div>
            {activeUpload && activeUpload.status !== UPLOAD_STATUS.COMPLETED && (
                <div className="mt-2 text-xs text-amber-800">
                    Uploading <span className="font-mono">{activeUpload.filename}</span>
                    {' '}— {Math.round(activeUpload.progress || 0)}%
                </div>
            )}
        </li>
    );
}

export const StalledUploadsBanner = ({ orderId, refreshKey, onRefresh }) => {
    const [sessions, setSessions] = useState([]);

    const fetchStalled = useCallback(async () => {
        if (!orderId) return;
        try {
            const res = await filesApi.getStalledUploads(orderId);
            setSessions(Array.isArray(res.data) ? res.data : []);
        } catch {
            // Non-fatal — silently keep last known state.
        }
    }, [orderId]);

    useEffect(() => {
        fetchStalled();
    }, [fetchStalled, refreshKey]);

    const handleDismiss = useCallback((sessionId) => {
        setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    }, []);

    const handleDismissAndRefresh = useCallback(() => {
        // Re-upload finished: refetch the banner (the row will disappear)
        // AND tell the parent to refresh its files list.
        fetchStalled();
        if (typeof onRefresh === 'function') onRefresh();
    }, [fetchStalled, onRefresh]);

    if (!sessions.length) return null;

    return (
        <div
            className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 space-y-3"
            data-testid="stalled-uploads-banner"
        >
            <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div>
                    <p className="text-sm font-semibold text-amber-900">
                        {sessions.length === 1
                            ? 'Upload interrupted'
                            : `${sessions.length} uploads interrupted`}
                    </p>
                    <p className="text-xs text-amber-800">
                        The chunked upload below didn&apos;t finish — click <strong>Re-upload</strong> to
                        select the file again, or <strong>Dismiss</strong> if you no longer need it.
                    </p>
                </div>
            </div>
            <ul className="space-y-2">
                {sessions.map((s) => (
                    <StalledUploadRow
                        key={s.id}
                        session={s}
                        orderId={orderId}
                        onDismiss={handleDismiss}
                        onDismissAndRefresh={handleDismissAndRefresh}
                    />
                ))}
            </ul>
        </div>
    );
};

export default StalledUploadsBanner;
